import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, CreditCardIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from './CtaButton';
import { MOCKUP_IMAGE, CHECKOUT_MEGAPACK } from '../data/content';
const trustBadges: {
  icon: React.ReactNode;
  label: string;
}[] = [{
  icon: <LockIcon className="h-4 w-4" aria-hidden="true" data-id="element-127" data-mp="4mi2n8mfxkdf" />,
  label: 'Pago seguro'
}, {
  icon: <ZapIcon className="h-4 w-4" aria-hidden="true" data-id="element-128" data-mp="1297ba1bnev89" />,
  label: 'Acceso en 5 minutos'
}, {
  icon: <ShieldCheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-129" data-mp="xannrwhepbds" />,
  label: 'Garantía 7 días'
}, {
  icon: <CreditCardIcon className="h-4 w-4" aria-hidden="true" data-id="element-130" data-mp="sbb57nxkiqh0" />,
  label: 'Mercado Pago'
}];
const subheadItems: string[] = ['6 Tomos + 6 Bonos', '480+ páginas listas para imprimir', 'Tomo 1 → Tomo 6', 'Todos los niveles', '100% vocabulario argentino', 'Acceso en 5 minutos'];
export function Hero() {
  return <header className="relative w-full overflow-hidden bg-navy-deep text-white" data-id="element-131" data-mp="3825xqp13i5ku">
      <div className="pointer-events-none absolute inset-0 opacity-[0.12]" aria-hidden="true" style={{
      backgroundImage: 'radial-gradient(circle at 20% 20%, #5FB0E5 0, transparent 45%), radial-gradient(circle at 85% 15%, #F5B301 0, transparent 40%)'
    }} data-id="element-132" data-mp="3j8sgk46tfprf" />
      <div className="relative mx-auto grid max-w-6xl gap-10 px-5 py-12 sm:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-8 lg:py-20" data-id="element-133" data-mp="3e8ms0wvuejdk">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-134" data-mp="30fy7fqvvirv4">
          <span className="inline-flex max-w-xl rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-celeste-light ring-1 ring-white/20 sm:text-[13px]" data-id="element-135" data-mp="2ql692kugw0za">
            🇦🇷 El sistema N°1 de lectoescritura para familias y docentes de Argentina
          </span>

          <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] text-balance sm:text-5xl lg:text-6xl" data-id="element-136" data-mp="2ljpef72ktar5">
            Todo tu año de{' '}
            <span className="text-gold" data-id="element-137" data-mp="nbbcixheu94r">Caligrafía y Lectoescritura</span>.
            <br data-id="element-138" data-mp="na060pwkyn9y" />
            Listo. Desde hoy.
          </h1>

          <ul className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-bold text-celeste-light sm:text-base" data-id="element-139" data-mp="id9v0gzmuhve">
            {subheadItems.map((item, index) => <li key={item} className="flex items-center gap-2" data-id="element-140" data-mp="1b1fgx72facwt" data-mpi={index}>
                {item}
                {index < subheadItems.length - 1 && <span className="text-white/30" aria-hidden="true" data-id="element-141" data-mp="162cd1py6icqf">
                    ·
                  </span>}
              </li>)}
          </ul>

          <p className="mt-5 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg" data-id="element-142" data-mp="x0cqyj8b4igm">
            Actividades + diplomas + guías listas para usar.{' '}
            <strong className="font-bold text-white" data-id="element-143" data-mp="3hrlm2muo2fb3">
              Sin buscar. Sin armar. Sin perder domingos.
            </strong>
          </p>

          <div className="mt-8" data-id="element-144" data-mp="1xama741tsf59">
            <CtaButton href={CHECKOUT_MEGAPACK} className="w-full sm:w-auto" data-id="element-145" data-mp="1x7htx8b35sy8">
              → Quiero mi MegaPack ahora
            </CtaButton>
          </div>

          <ul className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-semibold text-white/75 sm:text-sm" data-id="element-146" data-mp="2697m4gcskvx2">
            {trustBadges.map(badge => <li key={badge.label} className="inline-flex items-center gap-1.5" data-id="element-147" data-mp="2g0bzjbmtk8ps">
                <span className="text-celeste" data-id="element-148" data-mp="2kyvn1gh29sk9">{badge.icon}</span>
                {badge.label}
              </li>)}
          </ul>
        </motion.div>

        <motion.div className="relative" initial={{
        opacity: 0,
        scale: 0.94
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.6,
        delay: 0.15,
        ease: 'easeOut'
      }} data-id="element-149" data-mp="2p1a6o2sk0wcl">
          <div className="absolute inset-6 rounded-[40px] bg-celeste/25 blur-3xl" aria-hidden="true" data-id="element-150" data-mp="uou4bd2vq2d0" />
          <div className="relative mx-auto w-full max-w-md overflow-hidden rounded-[32px] ring-1 ring-white/15 lg:max-w-none" data-id="element-151" data-mp="4akyn39aysi3">
            <img src={MOCKUP_IMAGE} alt="MegaPack Caligrafía y Lectoescritura: libro principal con fichas de vocales, consonantes, sílabas, cursiva, lectura y diplomas imprimibles" className="block w-full" width={1024} height={1024} data-id="element-152" data-mp="3vh5k87gl987q" />
          </div>
        </motion.div>
      </div>
    </header>;
}